/**
 * Общая модалка правки текста бота.
 *
 * Раньше «править текст» с любой страницы вело на «Контент бота» — админ
 * попадал в общий список из 342 ключей и искал нужный руками. Теперь тот же
 * редактор открывается поверх текущей страницы по конкретному ключу.
 *
 * Использование:
 *   const [editKey, setEditKey] = useState<string | null>(null);
 *   <TextEditModal contentKey={editKey} onClose={() => setEditKey(null)} />
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button, Modal } from "@/components/ui";
import { popup } from "@/components/popup";

interface ContentItem {
  key: string;
  lang: string;
  default_value: string;
  value: string | null;
  media_url: string | null;
  overridden: boolean;
  updated_at: string | null;
}

/**
 * Что означает каждая подстановка — человеческим языком.
 * Текстами занимается не программист: «{pct}» ему ничего не говорит.
 */
const PLACEHOLDER_HELP: Record<string, { what: string; example: string }> = {
  "{pct}": { what: "размер скидки", example: "10" },
  "{code}": { what: "промокод юзера", example: "NLXK7M2P" },
  "{days}": { what: "сколько дней", example: "30" },
  "{days_text}": { what: "дни словами", example: "3 дня" },
  "{date}": { what: "дата", example: "31.08.2026" },
  "{price}": { what: "цена", example: "299" },
  "{balance}": { what: "баланс юзера", example: "150" },
  "{name}": { what: "имя юзера", example: "Иван" },
  "{link}": { what: "ссылка на подключение", example: "tg://proxy?..." },
  "{gb}": { what: "гигабайты", example: "50" },
  "{stars}": { what: "цена в звёздах", example: "250" },
  "{id}": { what: "номер", example: "42" },
  "{product}": { what: "что купил", example: "VPN" },
  "{hours}": { what: "сколько часов", example: "48" },
  "{tariff_name}": { what: "название тарифа", example: "NoLags Proxy" },
  "{expires}": { what: "когда закончится", example: "31.08.2026 14:30" },
  "{text}": { what: "текст сообщения", example: "…" },
  "{count}": { what: "количество", example: "5" },
};

/** Короткая подпись = кнопка: у неё другое превью и лимит длины. */
function isButton(key: string, text: string): boolean {
  return /_btn$|\.btn|button|menu\.|back_to_menu|\.back/.test(key) || text.length < 40;
}

/** Телеграм-разметка: <b>, <code>, ссылки, плейсхолдеры. */
function renderTg(src: string): string {
  let s = src.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  for (const tag of ["b", "strong", "i", "em", "u", "s", "code", "pre", "blockquote"]) {
    s = s
      .replace(new RegExp(`&lt;${tag}&gt;`, "g"), `<${tag}>`)
      .replace(new RegExp(`&lt;/${tag}&gt;`, "g"), `</${tag}>`);
  }
  s = s.replace(
    /&lt;a href=(?:&quot;|")(.*?)(?:&quot;|")&gt;(.*?)&lt;\/a&gt;/g,
    '<a href="$1" class="tg-link">$2</a>',
  );
  s = s.replace(/\{[a-zA-Z0-9_]+\}/g, '<span class="tg-ph">$&</span>');
  return s.replace(/\n/g, "<br/>");
}

export function TextEditModal({
  contentKey,
  title,
  lang = "ru",
  onClose,
  onSaved,
}: {
  contentKey: string | null;
  /** Человеческое название («Скидка на час»). Без него в шапке технический
   *  ключ funnel.offer_60m — админу, который правит тексты, он ничего не говорит. */
  title?: string;
  lang?: string;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const qc = useQueryClient();
  const [text, setText] = useState("");

  // Тянем ТОЛЬКО нужный ключ: грузить все 342 ради одной правки незачем.
  const q = useQuery<{ items: ContentItem[] }>({
    queryKey: ["bot-content-one", contentKey, lang],
    queryFn: () => api(`/api/admin/bot-content?lang=${lang}`),
    enabled: !!contentKey,
  });

  const item = q.data?.items.find((i) => i.key === contentKey) ?? null;
  const saved = item ? (item.value ?? item.default_value) : "";

  useEffect(() => {
    if (item) setText(item.value ?? item.default_value);
  }, [item?.key, item?.value]);

  const save = useMutation({
    mutationFn: () =>
      api("/api/admin/bot-content", {
        method: "PUT",
        body: JSON.stringify({ key: contentKey, lang, value: text }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["bot-content"] });
      qc.invalidateQueries({ queryKey: ["bot-content-one"] });
      qc.invalidateQueries({ queryKey: ["funnel"] });
      popup({
        tone: "success",
        title: "Сохранено",
        body: "Бот подхватит правку за ~15 секунд.",
      });
      onSaved?.();
      onClose();
    },
    onError: (e) => popup({ tone: "error", title: "Не сохранилось", body: (e as Error).message }),
  });

  const reset = useMutation({
    mutationFn: () =>
      api("/api/admin/bot-content/reset", {
        method: "POST",
        body: JSON.stringify({ key: contentKey, lang }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["bot-content"] });
      qc.invalidateQueries({ queryKey: ["bot-content-one"] });
      qc.invalidateQueries({ queryKey: ["funnel"] });
      popup({ tone: "info", title: "Вернули исходный текст", body: "" });
      onSaved?.();
      onClose();
    },
    onError: (e) => popup({ tone: "error", title: "Ошибка", body: (e as Error).message }),
  });

  const btn = isButton(contentKey ?? "", text);
  const dirty = text !== saved;
  // Плейсхолдеры вроде {code} обязаны остаться — иначе юзер получит текст
  // с дыркой вместо промокода.
  const holders = (saved.match(/\{[a-zA-Z0-9_]+\}/g) ?? []) as string[];
  const lost = holders.filter((h) => !text.includes(h));

  return (
    <Modal
      open={!!contentKey}
      onClose={onClose}
      title={title || (contentKey ? `Правка · ${contentKey}` : "")}
      maxWidth="max-w-3xl"
    >
      {q.isLoading ? (
        <div className="text-zinc-400 text-sm">Загрузка…</div>
      ) : !item ? (
        <div className="text-zinc-500 text-sm">Текст с ключом {contentKey} не найден.</div>
      ) : (
        <div className="space-y-4">
          {title && (
            <div className="text-[11px] text-zinc-600 -mt-1">
              ключ: <code>{contentKey}</code>
            </div>
          )}

          <div>
            <div className="text-xs text-zinc-500 mb-1.5">Как увидит юзер</div>
            {btn ? (
              <div className="inline-flex items-center rounded-lg bg-[#2b5278] text-zinc-50 text-sm px-3.5 py-2 max-w-full">
                <span dangerouslySetInnerHTML={{ __html: renderTg(text) }} />
              </div>
            ) : (
              <div
                className="rounded-2xl rounded-tl-sm bg-[#182533] border border-white/[0.05] px-3.5 py-2.5 text-sm text-zinc-100 leading-relaxed break-words max-w-lg"
                dangerouslySetInnerHTML={{ __html: renderTg(text) }}
              />
            )}
          </div>

          <div>
            <div className="flex items-baseline justify-between mb-1.5">
              <span className="text-xs text-zinc-500">Текст</span>
              {item.overridden && (
                <span className="text-[11px] text-amber-400">изменён вручную</span>
              )}
            </div>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={btn ? 2 : 8}
              className="w-full rounded-xl bg-black/25 border border-white/[0.08] px-3 py-2.5 text-sm text-zinc-100 font-mono leading-relaxed focus:outline-none focus:border-brand/50"
            />
          </div>

          {holders.length > 0 && (
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
              <div className="text-xs text-zinc-400 mb-2">
                Эти кусочки бот заменит на настоящие значения — не удаляйте их:
              </div>
              <div className="space-y-1.5">
                {holders.map((h) => {
                  const info = PLACEHOLDER_HELP[h];
                  const present = text.includes(h);
                  return (
                    <div key={h} className="flex items-baseline gap-2.5 text-xs">
                      <code
                        className={`shrink-0 ${
                          present ? "text-brand" : "text-rose-400 line-through"
                        }`}
                      >
                        {h}
                      </code>
                      <span className="text-zinc-400">
                        {info ? info.what : "значение из бота"}
                      </span>
                      {info && (
                        <span className="text-zinc-600">
                          например: {info.example}
                        </span>
                      )}
                      {!present && (
                        <span className="text-rose-400 ml-auto">удалён</span>
                      )}
                    </div>
                  );
                })}
              </div>
              {lost.length > 0 && (
                <div className="text-rose-400 text-xs mt-2.5 pt-2.5 border-t border-white/[0.06]">
                  Верните удалённые — иначе на их месте у юзера будет пусто.
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 pt-1">
            <Button
              variant="primary"
              disabled={!dirty || save.isPending || lost.length > 0}
              onClick={() => save.mutate()}
            >
              Сохранить
            </Button>
            <Button variant="ghost" onClick={onClose}>
              Отмена
            </Button>
            {item.overridden && (
              <Button
                variant="ghost"
                className="ml-auto"
                disabled={reset.isPending}
                onClick={() => reset.mutate()}
              >
                Вернуть исходный
              </Button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
